// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Carte, Chargement, Erreur } from '../../composants/communs.tsx';
import { Montant } from '../../composants/ui/index.ts';
import type { ReponseVentes } from '../ventes/types.ts';
import { LignesRetour } from './LignesRetour.tsx';
import { MOTIF_MIN, type Destination, type Remboursement, type Retournable } from './types.ts';

/**
 * Saisie d'un retour — Q-039 à Q-042.
 *
 * Identifiant et clé d'idempotence sont fixés À L'OUVERTURE : un double clic
 * ne rend pas la marchandise deux fois (RG-008).
 *
 * L'écran demande au serveur ce qui reste reprenable AVANT toute saisie. Le
 * vendeur voit donc des quantités déjà diminuées des retours précédents, et
 * sait si la fenêtre est passée — au lieu de se faire refuser après coup,
 * devant le client.
 */
export function FormulaireRetour({ onFerme }: { onFerme: () => void }): ReactNode {
  const { boutiqueId, boutiques, peut } = useSession();
  const client = useQueryClient();

  const [identite] = useState(() => ({ id: cleNeuve(), cle: cleNeuve() }));
  const [boutique, setBoutique] = useState<string>(boutiqueId ?? boutiques[0]?.id ?? '');
  const [venteId, setVenteId] = useState('');
  const [motif, setMotif] = useState('');
  const [remboursement, setRemboursement] = useState<Remboursement>('AVOIR');
  const [quantites, setQuantites] = useState<Record<string, string>>({});
  const [destinations, setDestinations] = useState<Record<string, Destination>>({});

  const ventes = useQuery({
    queryKey: ['ventes', boutique],
    queryFn: () => appeler<ReponseVentes>(`/ventes?boutique_id=${boutique}`),
    enabled: boutique !== '',
    // Une vente contrepassée n'a plus rien à rendre : elle est déjà annulée.
    select: (reponse) => reponse.donnees.filter((vente) => vente.statut === 'VALIDEE'),
  });

  const etat = useQuery({
    queryKey: ['retournable', venteId],
    queryFn: () => appeler<Retournable>(`/ventes/${venteId}/retournable`),
    enabled: venteId !== '',
  });

  const reprenables = (etat.data?.lignes ?? []).filter((ligne) => ligne.quantite_retournable > 0);

  const saisies = reprenables
    .map((ligne) => ({ ligne, quantite: Number(quantites[ligne.vente_ligne_id] ?? '0') }))
    .filter((saisie) => saisie.quantite > 0);

  const montant = saisies.reduce(
    (somme, saisie) => somme + saisie.ligne.prix_unitaire_net * saisie.quantite,
    0,
  );
  const trop = saisies.some((saisie) => saisie.quantite > saisie.ligne.quantite_retournable);

  // Hors fenêtre SANS la permission : le serveur refusera. Le dire ici évite
  // de saisir un retour entier pour rien.
  const bloqueParFenetre = (etat.data?.hors_fenetre ?? false) && !peut('retour.hors_fenetre');

  const complet =
    boutique !== '' &&
    venteId !== '' &&
    saisies.length > 0 &&
    !trop &&
    !bloqueParFenetre &&
    motif.trim().length >= MOTIF_MIN;

  const envoi = useMutation({
    mutationFn: () =>
      appeler<{ numero: string }>('/retours', {
        methode: 'POST',
        cleIdempotence: identite.cle,
        corps: {
          id: identite.id,
          vente_id: venteId,
          boutique_id: boutique,
          motif: motif.trim(),
          remboursement,
          lignes: saisies.map((saisie) => ({
            vente_ligne_id: saisie.ligne.vente_ligne_id,
            quantite: saisie.quantite,
            destination: destinations[saisie.ligne.vente_ligne_id] ?? 'STOCK',
          })),
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['retours'] });
      // Le stock et les avoirs bougent aussi : les laisser en cache montrerait
      // un rayon et un solde périmés.
      void client.invalidateQueries({ queryKey: ['stock'] });
      void client.invalidateQueries({ queryKey: ['clients'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre="Nouveau retour"
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="retour-boutique">Boutique où le client se présente</label>
          <select
            id="retour-boutique"
            value={boutique}
            onChange={(evenement) => {
              setBoutique(evenement.target.value);
              setVenteId('');
              setQuantites({});
            }}
          >
            {boutiques.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
        </div>

        <div className="champ">
          <label htmlFor="retour-vente">Vente d’origine</label>
          <select
            id="retour-vente"
            value={venteId}
            onChange={(evenement) => {
              setVenteId(evenement.target.value);
              setQuantites({});
            }}
          >
            <option value="">— choisir la vente —</option>
            {(ventes.data ?? []).map((vente) => (
              <option key={vente.id} value={vente.id}>
                {vente.numero} — {new Date(vente.horodatage_serveur).toLocaleDateString('fr-FR')}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Erreur erreur={ventes.error} />
      <Erreur erreur={etat.error} />
      {venteId !== '' && etat.isPending && <Chargement />}

      {etat.data !== undefined && (
        <>
          <p>
            Vendue il y a {etat.data.jours_ecoules} jour(s) · fenêtre de{' '}
            {etat.data.fenetre_jours} jour(s)
            {etat.data.hors_fenetre && (
              <strong>
                {' '}
                · HORS FENÊTRE
                {bloqueParFenetre
                  ? " — l'accord d'un responsable est nécessaire."
                  : ' — vous pouvez déroger, ce sera tracé.'}
              </strong>
            )}
          </p>

          <LignesRetour
            lignes={reprenables}
            quantites={quantites}
            destinations={destinations}
            onQuantite={(ligneId, valeur) => {
              setQuantites((avant) => ({ ...avant, [ligneId]: valeur }));
            }}
            onDestination={(ligneId, valeur) => {
              setDestinations((avant) => ({ ...avant, [ligneId]: valeur }));
            }}
          />
        </>
      )}

      <div className="champs" style={{ marginTop: 12 }}>
        <div className="champ">
          <label htmlFor="retour-remboursement">Forme du remboursement</label>
          <select
            id="retour-remboursement"
            value={remboursement}
            onChange={(evenement) => {
              setRemboursement(evenement.target.value as Remboursement);
            }}
          >
            <option value="AVOIR">Avoir</option>
            {peut('retour.rembourser_especes') && <option value="ESPECES">Espèces</option>}
          </select>
          <small>
            Si la vente porte encore une dette, le serveur la réduit d’abord : rendre de
            l’argent à qui en doit encore reviendrait à financer son crédit.
          </small>
        </div>

        <div className="champ">
          <label htmlFor="retour-motif">Motif ({MOTIF_MIN} caractères minimum)</label>
          <textarea
            id="retour-motif"
            rows={2}
            maxLength={500}
            value={motif}
            onChange={(evenement) => {
              setMotif(evenement.target.value);
            }}
          />
        </div>
      </div>

      <Erreur erreur={envoi.error} />
      <div className="ligne" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={!complet || envoi.isPending}
          onClick={() => {
            envoi.mutate();
          }}
        >
          {envoi.isPending ? 'Enregistrement…' : 'Enregistrer le retour'}
        </button>
        <span style={{ marginLeft: 12 }}>
          Montant repris : <Montant valeur={montant} />
        </span>
      </div>
    </Carte>
  );
}
